import React, { useRef, useState, useEffect } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import _ from 'lodash';
import ReactToPrint from 'react-to-print';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Col, Row, Spin, Typography } from 'antd';
import { useMount } from 'react-use';
import { PrinterOutlined, ArrowLeftOutlined, PushpinOutlined } from '@ant-design/icons';
import { deliveryLatAtom, deliveryLonAtom, mapZoomAtom, openDeliveyMapAtom, ordersAtom } from '../../../atoms/Atoms';

import { getCardType, getUserPlatform } from '../../utils/functions';
import api from '../../../api/api';
import DeliveryMap from '../../shared-components/modals/DeliveryMap';

function DeliveryDetails() {
  const ref = useRef(null);
  const [order, setOrders] = useRecoilState(ordersAtom);
  const history = useHistory();
  const [loadingItems, setLoadingItems] = useState(false);
  const [orderItems, setOrderItems] = useState(null);
  const [deliveryLat, setDeliveryLat] = useRecoilState(deliveryLatAtom);
  const [deliveryLon, setDeliveryLon] = useRecoilState(deliveryLonAtom);
  const [zoomLevel, setZoomLevel] = useRecoilState(mapZoomAtom);
  const setOpenMap = useSetRecoilState(openDeliveyMapAtom);
  const { t } = useTranslation();
  function mapOrderItems(data) {
    const sorted = _.map(data, (p) => ({
      key: p.id,
      id: p.id,
      completeName: p.completeName,
      name: p.name,
      brand: p.brand,
      amount: p.amount,
      weightDescriptor: p.weightDescriptor,
      desiredDateTime: p.desiredDateTime,
      upc: p.upc,
      aisle: p.aisle,
      price: p.price,
      description: p.description,
      extendedPrice: p.extendedPrice,
      itemImage: p.itemImage,
      hasRandomWeight: p.hasRandomWeight,
      sectionName: p.sectionName,
      isAlcoholicBeverage: p.isAlcoholicBeverage,
      isPacked: p.isPacked,
      isPerishable: p.isPerishable,
      isVoided: p.isVoided,
      manuallyAdded: p.manuallyAdded,
      acceptsSubstitutes: p.acceptsSubstitutes,
    }));
    return _.sortBy(sorted, [(o) => [o.isVoided, o.brand, o.name]]);
  }

  function loadItems() {
    setLoadingItems(true);
    api
      .get(`orders/${order?.id}/items/all`)
      .then((response) => {
        const map = mapOrderItems(response.data.data.results);
        setOrderItems(map);
        setLoadingItems(false);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  useMount(() => {
    setOrderItems(null);
    loadItems();
    // setOpenMap(false);
  });

  useEffect(() => {
    loadItems();
    setDeliveryLat(order?.customerLocationLat);
    setDeliveryLon(order?.customerLocationLon);
    setZoomLevel(15);
  }, [order]);

  return (
    <main ref={ref} className="max-w-7xl mx-auto px-4 py-8 space-y-8">
      <Button
        shape="circle"
        size="small"
        icon={<ArrowLeftOutlined />}
        onClick={() => {
          history.push('/deliveries');
        }}
      />
      <section className="text-3xl font-extrabold tracking-tight">
        {t('order_details')}
      </section>
      <section className="flex mb-2 items-center text-sm border-b border-gray-200 sm:flex sm:justify-between">
        <Col>
          <Row>
            <Typography.Text className="font-bold">{t('service')}</Typography.Text>
          </Row>
          <Row>
            <div className="text-gray-500 mr-1">{t('type')}:</div>
            <div className="font-medium">{order?.type}</div>
          </Row>
          <Row>
            <div className="text-gray-500 mr-1">{t('contact')}:</div>
            <div className="font-medium">{order?.customerPhone}</div>
          </Row>
          <Row>
            <div className="text-gray-500 mr-1">{t('customer')}:</div>
            <div className="font-medium">{order?.customer}</div>
          </Row>
          <Row>
            <div className="text-gray-500 mr-1">{t('confirmation_code')}:</div>
            <div className="font-medium">{order?.confirmationCode}</div>
          </Row>
          <Row>
            <div className="text-gray-500 mr-1">{t('date')}:</div>
            <div className="font-medium">{order?.desiredDateTime}</div>
          </Row>
          <Row>
            <div className="text-gray-500 mr-1">{t('store')}:</div>
            <div className="font-medium">{order?.businessName}</div>
          </Row>
        </Col>
        <Col>
          <Row>
            <Typography.Text className="font-bold">{t('location')}</Typography.Text>
          </Row>
          <Row>
            <div className="font-medium">{order?.customerLocationName}</div>
          </Row>
          <Row>
            <div className="font-medium">{order?.customerLocationAddress}</div>
          </Row>
          <Row>
            <div className="font-medium">{`${order?.customerLocationCity}, ${order?.customerLocationZipcode}`}</div>
          </Row>
          <Row>
            <div className="text-gray-500 mr-1">{t('instructions')}</div>
          </Row>
          <Row>
            <div className="font-medium">{`${order?.customerLocationInstructions}`}</div>
          </Row>
          <Row hidden>
            <div className="text-gray-500 mr-1">{t('platform')}:</div>
            <div className="font-medium">{getUserPlatform(order?.userPlatform)}</div>
          </Row>
          <Row hidden>
            <div className="text-gray-500 mr-1">{t('zone')}:</div>
            <div className="font-medium">{order?.deliveryZoneName}</div>
          </Row>
        </Col>
        <div>
          <ReactToPrint
            content={() => ref.current}
            trigger={() => (
              <button
                type="button"
                className="flex items-center text-white text-xl justify-center bg-blue-400 hover:bg-blue-200 rounded-full w-10 h-10 ring-0 focus:ring-0 outline-none focus:outline-none transition duration-500 ease-in"
              >
                <PrinterOutlined className="h-5 w-5" />
              </button>
            )}
          />
          <Button
            className="flex items-center text-white mt-4 text-xl justify-center bg-green-400 hover:bg-green-200 rounded-full w-10 h-10 ring-0 focus:ring-0 outline-none focus:outline-none transition duration-500 ease-in"
            type="link"
            onClick={() => {
              setOpenMap(true);
            }}
          >
            <PushpinOutlined className="h-5 w-5" />
          </Button>
        </div>
      </section>
      <section className="space-y-4">
        <div className="text-xl font-extrabold tracking-tight">
          {t('items')}({t('included')}): {order && order.itemCount} {t('out_of')} {orderItems && orderItems.length}
          <br />
          {loadingItems ? <Spin /> : null }
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:md:grid-cols-4 gap-4">
          {_.map(orderItems, (product) => (
            <div
              key={product.id}
              className="flex items-center border rounded-xl space-x-4 p-4"
            >
              <div className="flex items-center justify-center w-20 h-20 border rounded-xl p-2">
                <img
                  src={product.itemImage}
                  alt={product.name}
                  className="object-center object-cover"
                />
              </div>
              <div className="flex-1">
                <div className="text-xs font-medium">{product.name}</div>
                <div className="text-xs text-gray-500">{product.brand}</div>
                <div className="text-xs text-gray-500">
                  {product.description}
                </div>
              </div>
              <div className="flex flex-col text-sm font-medium">
                <div className="flex flex-row">
                  <div className="text-xs text-gray-600">x</div>
                  <div className="text-sm">{product.amount.toFixed(2)}</div>
                  <div className="text-sm lowercase">{product.hasRandomWeight ? product.weightDescriptor : ''}</div>
                </div>
                <div>${product.price.toFixed(2)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
      <section>
        <div className="bg-gray-50 rounded-xl py-6 px-6 lg:px-0 lg:py-8 lg:grid lg:grid-cols-12 lg:gap-x-8">
          <dl className="grid grid-cols-1 gap-6 text-sm sm:grid-cols-2 md:gap-x-8 lg:pl-8 lg:col-span-5">
            <div>
              <dt className="font-medium text-gray-900">
                {t('payment')}
              </dt>
              <dd className="mt-3 flex">
                <div>
                  <img
                    alt={
                      order?.nickName ? order?.nickName : order?.paymentName
                    }
                    src={
                    order?.nickName
                      ? getCardType(order?.brand)
                      : order?.paymentImageUrl
                    }
                    className="h-4 w-auto"
                  />
                  <p className="sr-only">
                    {order?.nickName ? order?.nickName : order?.paymentName}
                  </p>
                </div>
                <div className="ml-4">
                  {order?.nickName ? order?.nickName : order?.paymentName}
                </div>
              </dd>
            </div>
          </dl>
          <dl className="mt-4 divide-y divide-gray-200 text-sm lg:mt-0 lg:pr-8 lg:col-span-7">
            <div className="pb-4 flex items-center justify-between">
              <dt className="text-gray-600">{t('subtotal')}</dt>
              <dd className="font-medium text-gray-900">
                ${order?.subTotal}
              </dd>
            </div>
            <div className="py-4 flex items-center justify-between">
              <dt className="text-gray-600">{t('municipal_tax')}</dt>
              <dd className="font-medium text-gray-900">
                ${order?.munTax}
              </dd>
            </div>
            <div className="py-4 flex items-center justify-between">
              <dt className="text-gray-600">{t('state_tax')}</dt>
              <dd className="font-medium text-gray-900">
                ${order?.stateTax}
              </dd>
            </div>
            <div className="py-4 flex items-center justify-between">
              <dt className="text-gray-600">{t('service_cost')}</dt>
              <dd className="font-medium text-gray-900">
                ${order?.processingFee}
              </dd>
            </div>
            <div className="py-4 flex items-center justify-between">
              <dt className="text-gray-600">{t('delivery_cost')}</dt>
              <dd className="font-medium text-gray-900">
                ${order?.deliveryTotal.toFixed(2)}
              </dd>
            </div>
            <div className="pt-4 flex items-center justify-between">
              <dt className="font-medium text-gray-900">{t('total')}</dt>
              <dd className="font-medium font-bold text-gray-900">
                ${order?.total}
              </dd>
            </div>
          </dl>
        </div>
        <DeliveryMap />
      </section>
    </main>
  );
}

export default DeliveryDetails;
